import NextAuth from "next-auth"
import KeycloakProvider from "next-auth/providers/keycloak"
import { authConfig } from "./auth.config"

declare module "next-auth" {
  interface Session {
    accessToken?: string
    refreshToken?: string
    djangoAccess?: string
    djangoRefresh?: string
    error?: string
  }
}

declare module "next-auth/jwt" {
  interface JWT {
    accessToken?: string
    refreshToken?: string
    expiresAt?: number
    djangoAccess?: string
    djangoRefresh?: string
    djangoExpiresAt?: number
    error?: string
  }
}


async function refreshDjangoToken(token: any) {
  try {
    const refreshToken = token.djangoRefresh
    if (!refreshToken) {
      console.warn("No refresh token available")
      return { ...token, error: "RefreshDjangoTokenError" }    
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/token/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh: refreshToken }),
    })

    if (!response.ok) {
      console.warn(`Failed to refresh Django token. Status: ${response.status}`)
      return { ...token, error: "RefreshDjangoTokenError" }    
    }

    const data = await response.json()

    return {
      ...token,
      djangoAccess: data.access,
      djangoRefresh: data.refresh ?? token.djangoRefresh, // Django might not return new refresh
      djangoExpiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000)
    }
  } catch (error) {
    console.error("Error refreshing Django token:", error)
    return {
      ...token,
      error: "RefreshDjangoTokenError",
    }
  }
}

export const {
  handlers: { GET, POST },
  auth,
  signIn,
  signOut,
} = NextAuth({
  ...authConfig,
  secret: process.env.AUTH_SECRET,
  providers: [
    KeycloakProvider({
      clientId: process.env.KEYCLOAK_CLIENT_ID!,
      clientSecret: process.env.KEYCLOAK_CLIENT_SECRET || "public",
      issuer: "https://identity.hartis.org/realms/ogc-hub",
    }),
  ],
  session: { strategy: "jwt" },
  callbacks: {
    async jwt({ token, account, user }) {
      // Initial sign in — exchange Keycloak → Django
      if (account && user) {
        const kcAccess = account.access_token as string

        try {
          const res = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/oidc/exchange/`, {
            method: "POST",
            headers: { Authorization: `Bearer ${kcAccess}` },
          })

          if (res.ok) {
            const data = await res.json()
            
            token.djangoAccess = data.access
            token.djangoRefresh = data.refresh
            token.djangoExpiresAt = Date.now() + (15 * 60 * 60 * 1000) 
            
            token.user = {
              ...user,
              id: data.user?.id,
              username: data.user?.username,
              email: data.user?.email,
            }
          } else {
	      const errorText = await res.text(); 	  
	  }         
        } catch (err) {
          console.error("Django token exchange failed:", err)
        }

        token.accessToken = kcAccess
        token.refreshToken = account.refresh_token
        token.expiresAt = account.expires_at ? account.expires_at * 1000 : Date.now() + 300000
        return token
      }

      // Check if Django token needs refresh (check 1 minute before expiry)
      const shouldRefreshTime = (token.djangoExpiresAt as number) - 60 * 1000 - Date.now()
      
      if (shouldRefreshTime > 0) {
        return token // Still valid
      }

      // Django token expired or about to expire - refresh it
      if (token.djangoRefresh) {
        const refreshed = await refreshDjangoToken(token)
        if (refreshed.error) {
          return { ...token, error: "RefreshDjangoTokenError" }
        }
        return refreshed
      }

      return { ...token, error: "RefreshDjangoTokenError" }
    },

    async session({ session, token }) {
      session.user = token.user as any
      session.accessToken = token.accessToken
      session.refreshToken = token.refreshToken
      session.djangoAccess = token.djangoAccess
      session.djangoRefresh = token.djangoRefresh
      session.error = token.error
      return session
    },
  },
})